/**
 * src/components/ui/BloqueJSON.jsx
 * El recuadro de JSON crudo del panel, con Copiar y Descargar.
 *
 * Pedido por Manuel (31-08): el panel escupe JSON en cinco sitios —la foto fija
 * del lector de fichas, el censo de convenios, qué contestó cada tienda, el
 * diagnóstico del tenant y el resultado de un reenvío— y algunos son de decenas
 * de miles de caracteres. Seleccionarlos a mano en un `<pre>` con scroll es
 * incómodo y propenso a copiar solo un trozo.
 *
 * Una regla, un sitio (§4 de docs/ERRORES_RECURRENTES.md): los cinco pasan por
 * aquí, así que el día que cambie el trato del JSON cambia una vez.
 *
 * Dos decisiones que importan:
 *
 * 1. Se copia SIEMPRE el texto completo, no lo que se ve. El `<pre>` tiene
 *    `maxHeight`, y copiar la selección visible daría un JSON truncado que
 *    parece entero — la peor clase de fallo, el que no se nota.
 *
 * 2. El portapapeles puede fallar (contexto no seguro, permiso denegado) y
 *    entonces se DICE (§5, sin fallos mudos). Nunca se pinta «✓ Copiado» sin
 *    haber copiado. Hay respaldo con `execCommand` para navegadores viejos, y
 *    si tampoco puede, el aviso invita a seleccionarlo a mano.
 */
import { useEffect, useRef, useState } from 'react'

const PRE = {
  padding: 'var(--space-3)',
  background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)',
  fontFamily: 'var(--font-mono)', color: 'var(--color-text)',
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  overflowX: 'auto', margin: 'var(--space-3) 0 0',
}

const BOTON = {
  padding: 'var(--space-1) var(--space-3)', borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-xs)', fontFamily: 'var(--font-ui)',
  border: '1px solid var(--color-border)', background: 'transparent',
  color: 'var(--color-text-2)', cursor: 'pointer',
}

/** Bytes de verdad (UTF-8), no caracteres: un JSON con tildes y ñ pesa más de
    lo que mide `length`, y el número está para saber si cabe donde se pega. */
function _tamano(texto) {
  const bytes = typeof TextEncoder !== 'undefined'
    ? new TextEncoder().encode(texto).length
    : texto.length
  const lineas = texto.split('\n').length
  const kb = bytes / 1024
  const peso = kb < 1
    ? `${bytes} B`
    : `${kb.toLocaleString('es-CL', { maximumFractionDigits: 1 })} KB`
  return `${peso} · ${lineas.toLocaleString('es-CL')} líneas`
}

/** Respaldo para cuando no hay API de portapapeles: un textarea fuera de
    pantalla y el execCommand de toda la vida. Devuelve si lo consiguió. */
function _copiarALaAntigua(texto) {
  try {
    const ta = document.createElement('textarea')
    ta.value = texto
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.top = '-1000px'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

function _nombreDeArchivo(nombre) {
  const base = (nombre || 'panel').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'panel'
  // El día en Chile, que es el huso del panel (regla del 29-08).
  const dia = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' })
  return `${base}-${dia}.json`
}

/**
 * @param {any}     datos    el objeto a mostrar.
 * @param {string}  nombre   base del nombre del archivo al descargar.
 * @param {string}  maxAlto  alto máximo del recuadro (por defecto 420px).
 * @param {boolean} compacto sin sangría, todo en una línea. Es lo que quiere un
 *                           fixture destinado a pegarse: la sangría de 2 lo
 *                           hincha varias veces sin aportar nada a quien lo va
 *                           a versionar. Solo la foto fija lo usa.
 */
export function BloqueJSON({ datos, nombre = 'panel', maxAlto = '420px', compacto = false }) {
  const texto = compacto ? JSON.stringify(datos) : JSON.stringify(datos, null, 2)
  const [aviso, setAviso] = useState(null)   // {tipo:'ok'|'fallo', texto}
  const reloj = useRef(null)

  // Un aviso que sobrevive al desmontaje deja un setState al aire; y si se
  // pulsa dos veces seguidas, el reloj viejo borraría el aviso nuevo.
  useEffect(() => () => clearTimeout(reloj.current), [])

  function _decir(tipo, mensaje, ms = 2000) {
    clearTimeout(reloj.current)
    setAviso({ tipo, texto: mensaje })
    reloj.current = setTimeout(() => setAviso(null), ms)
  }

  async function copiar() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(texto)
        _decir('ok', '✓ Copiado')
        return
      }
      throw new Error('sin API de portapapeles')
    } catch {
      if (_copiarALaAntigua(texto)) { _decir('ok', '✓ Copiado'); return }
      _decir('fallo', '✗ No se pudo copiar — selecciónalo a mano', 6000)
    }
  }

  function descargar() {
    try {
      const url = URL.createObjectURL(new Blob([texto], { type: 'application/json' }))
      const a = document.createElement('a')
      a.href = url
      a.download = _nombreDeArchivo(nombre)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      // Sin esperar, algún navegador cancela la descarga al revocar la URL.
      setTimeout(() => URL.revokeObjectURL(url), 4000)
      _decir('ok', '✓ Descargado')
    } catch {
      _decir('fallo', '✗ No se pudo descargar — copia el texto', 6000)
    }
  }

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
        flexWrap: 'wrap', marginTop: 'var(--space-3)',
      }}>
        <button type="button" onClick={copiar} style={BOTON}>Copiar</button>
        <button type="button" onClick={descargar} style={BOTON}>Descargar .json</button>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
          color: 'var(--color-text-3)',
        }}>
          {_tamano(texto)}
        </span>
        {aviso && (
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
            color: aviso.tipo === 'ok' ? 'var(--color-ok)' : 'var(--color-error)',
          }}>
            {aviso.texto}
          </span>
        )}
      </div>
      <pre style={{
        ...PRE, maxHeight: maxAlto,
        // Una sola línea larguísima no tiene por dónde cortarse por palabras.
        wordBreak: compacto ? 'break-all' : 'break-word',
      }}>{texto}</pre>
    </div>
  )
}
